import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  BirthWeightRecording,
  birthWeightRecordingOf,
  FarmOverviewRow,
  FarmWeighing
} from '../../models/farms-overview';
import { AnchorRect, useAnchorRect } from '../../hooks/useAnchorRect';
import { HoverCard } from '../../elements/ui/hover-card';
import { pigletRegistrationColors as c } from '../PigletRegistration/colors';

const num = (value: number) => value.toLocaleString();

/**
 * One decimal, not whole percent. The tolerance is a couple of percent, so rounding would put an amber
 * badge and a green one on the same number and make the colour look arbitrary.
 */
const pct = (value: number) => `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;

/**
 * The numbers behind the badge, so a green one can be checked rather than just believed: what the farm
 * registered, how much of it was really weighed, and the share it is allowed to miss.
 */
const BirthWeightTooltip = ({
  recording,
  lookbackWeeks,
  tolerancePercent,
  anchor
}: {
  recording: BirthWeightRecording;
  lookbackWeeks: number;
  tolerancePercent: number | null;
  anchor: AnchorRect;
}) => {
  const { t } = useTranslation();

  return (
    <HoverCard anchor={anchor} className="max-w-72">
      <p className="mb-1.5 font-medium text-gray-700">{t('f-farms-overview-birth-weight-title')}</p>

      <p className="text-gray-500">
        {t('f-farms-overview-birth-weight-detail', {
          measured: num(recording.measured),
          registered: num(recording.registered),
          weeks: lookbackWeeks
        })}
      </p>

      {/* The one state the counts alone understate: nothing at all was weighed, which is a farm without a
          working scale rather than a farm that keeps forgetting. */}
      {recording.weighing === FarmWeighing.NoScale && (
        <p className="mt-2 text-gray-500">{t('f-farms-overview-birth-weight-no-scale-detail')}</p>
      )}

      {/* Only stated when the API sent the threshold. Naming a number the backend can retune without the
          frontend hearing about it would contradict the badge it is meant to explain. Turned round into the
          share weighed, so the rule is stated in the same terms as the badge above it. */}
      {tolerancePercent !== null && (
        <p className="mt-2 border-t border-gray-100 pt-1.5 text-gray-500">
          {t('f-farms-overview-birth-weight-threshold', { threshold: pct(100 - tolerancePercent) })}
        </p>
      )}
    </HoverCard>
  );
};

export interface FarmBirthWeightBadgeProps {
  farm: FarmOverviewRow;
  /** Weeks the figures cover, quoted in the tooltip. */
  lookbackWeeks: number;
  /** The share a farm may leave unweighed, as the API reports it; null when it did not. */
  tolerancePercent: number | null;
}

/**
 * Dot colour and text per weighing state.
 *
 * Amber, not red, for a farm that is merely behind: it is a weight-related measure, like the two weight
 * cards above the queue, and forgetting to weigh some piglets is something to follow up rather than the
 * alarm a farm registering nothing is. Red is spent only on the farm that weighed nothing at all, which is
 * not a degree of the same problem - the scale is unused, and no amount of reminding about individual
 * piglets fixes that.
 */
const WEIGHING_BADGE: Record<FarmWeighing, { color: string; labelKey: string }> = {
  [FarmWeighing.OnTrack]: { color: c.neutral, labelKey: 'f-farms-overview-birth-weight-badge' },
  [FarmWeighing.Behind]: { color: c.target, labelKey: 'f-farms-overview-birth-weight-badge' },
  [FarmWeighing.NoScale]: { color: c.belowExpected, labelKey: 'f-farms-overview-birth-weight-badge-no-scale' }
};

/**
 * Whether the farm really weighs the piglets it registers.
 *
 * The badge states the share that was weighed, not the share that was missed: it is the number the two
 * weight cards above the queue and the weighed-first sort orders are all phrased in, and "99.8% not
 * weighed" reads as a large number on a farm that is doing almost nothing.
 *
 * Weighed is not "has a birth weight recorded" - registering a piglet without one stores a weight
 * estimated from its weight category, so that reading would be green everywhere, including on a farm that
 * owns no scale. It means weights that came off the farm's own scale, within four days of birth.
 *
 * Renders nothing for a farm with no registrations in the period, and for a response that predates the
 * fields - see `birthWeightRecordingOf`.
 */
export default function FarmBirthWeightBadge({ farm, lookbackWeeks, tolerancePercent }: FarmBirthWeightBadgeProps) {
  const { t } = useTranslation();
  const wrapperRef = React.useRef<HTMLSpanElement>(null);
  const { anchor, openByPointer, openByFocus, clear } = useAnchorRect(wrapperRef);

  const recording = birthWeightRecordingOf(farm);

  if (!recording) return null;

  const badge = WEIGHING_BADGE[recording.weighing];

  return (
    // Focus opens the card as well as hover: the counts exist nowhere else on the screen, so reading them
    // cannot depend on having a pointer.
    <span
      ref={wrapperRef}
      tabIndex={0}
      onMouseEnter={openByPointer}
      onMouseLeave={clear}
      onFocus={openByFocus}
      onBlur={clear}
      className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-0.5 text-xs font-medium text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
    >
      <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: badge.color }} />
      {t(badge.labelKey, { percent: pct(recording.measuredPercent) })}

      {anchor && (
        <BirthWeightTooltip
          recording={recording}
          lookbackWeeks={lookbackWeeks}
          tolerancePercent={tolerancePercent}
          anchor={anchor}
        />
      )}
    </span>
  );
}
